import * as React from "react";
import classnames from "classnames";
import { BaseMapItem } from "./BaseMapItem";
import { IBasemap } from "../config";
import { inIframe } from "../Toc/helpers";
import { Map } from "ol";
import { Layer } from "ol/layer";
import { useRef, useEffect, useState } from "react";
import { useMapContext } from "../../Contexts/MapContext";
import { BaseMapButton } from "./BaseMapButton";
import { useBaseMapContext } from "../../Contexts/BaseMapContext";
import { ChildMapsPopup } from "./ChildMapsPopup";
import { BaseMapsMode } from "../enums";
import "./BaseMapWidget.scss";

export interface ICustomBaseMap {
    config: IBasemap;
    id?: string;
    title?: string;
    baseLayers?: any;
    parentId?: any;
    childMaps?: any;
    img?: any;
}

export interface BasemapSelector {
    basemapId: string;
    selectedMapId: string;
}

export const BaseMapWidget = (props: { map: Map }): JSX.Element => {
    const { configA, setBaseMap } = useMapContext();
    const { subBasemapConfig, clickedBasemap } = useBaseMapContext();

    let resizeTimeoutHandler: number;

    const useExpand =
        !configA.colapseBasemaps ||
        configA.colapseBasemaps === "none" ||
        (configA.colapseBasemaps === "iframe" && !inIframe());

    const basemapSelectors: BasemapSelector[] = [];

    const [expanded, setExpanded] = useState<boolean>(useExpand);
    const [previousWidth, setPreviousWidth] = useState<number>(0);

    const previousWidthRef = useRef<number>(previousWidth);
    const expandedRef = useRef<boolean>(expanded);
    const mobileRef = useRef<boolean>(false);

    const { primaryMapId, secondaryMapId, widgetMode, isOverlayModeActive, changeMap, secondMap, setSecondMap } =
        useBaseMapContext();

    useEffect(() => {
        if (useExpand) {
            mobileRef.current && toggleExpanded();
            window.addEventListener("resize", () => onWindowResize());
            onWindowResize();
        }
        return window.removeEventListener("resize", onWindowResize);
    }, []);

    useEffect(() => {
        expandedRef.current = expanded;
    }, [expanded]);

    useEffect(() => {
        previousWidthRef.current = previousWidth;
    }, [previousWidth]);

    useEffect(() => {
        setBaseMap(primaryMapId);
    }, [primaryMapId]);

    useEffect(() => {
        if (!secondaryMapId && secondMap) {
            secondMap.setZIndex(0);
            secondMap.setOpacity(1);
            secondMap.setVisible(false);
            setSecondMap(null);
            return;
        }
        if (widgetMode === BaseMapsMode.opacity) {
            if (secondaryMapId) {
                props.map
                    .getLayers()
                    .getArray()
                    .map((basemapLayer: Layer) => {
                        if (basemapLayer.get("id") === secondaryMapId) {
                            basemapLayer.setZIndex(1);
                            basemapLayer.setVisible(true);
                            setSecondMap(basemapLayer);
                        }
                    });
            }
        }
    }, [widgetMode, secondaryMapId]);

    const onWindowResize = () => {
        if (resizeTimeoutHandler) {
            window.clearTimeout(resizeTimeoutHandler);
        }

        resizeTimeoutHandler = window.setTimeout(() => {
            checkSize();
        }, 300);
    };

    const checkSize = () => {
        if (previousWidthRef.current === window.innerWidth) {
            return;
        }
        mobileRef.current = document.body.scrollWidth < (configA.basemapsCollapseThreshold || 544);

        setExpanded(!mobileRef.current);
        setPreviousWidth(window.innerWidth);
    };

    const toggleExpanded = () => {
        const newState = mobileRef.current || !useExpand ? !expandedRef.current : true;
        newState !== expandedRef.current && setExpanded(newState);
    };

    useEffect(() => {
        if (!clickedBasemap) {
            return;
        }
        clickedBasemap.id !== primaryMapId && changeMap(clickedBasemap.id);
        toggleExpanded();
    }, [clickedBasemap]);

    const getSelectedMapId = (basemap: ICustomBaseMap, selectedMapId: string): string => {
        const bmap = basemapSelectors.filter((val) => val.basemapId === basemap.id)[0];
        if (!bmap) {
            basemapSelectors.push({ basemapId: basemap.id, selectedMapId: selectedMapId });
            return basemap.id;
        }

        if (basemap.config.childMaps.filter((v) => v === selectedMapId).length > 0 || basemap.id === selectedMapId) {
            bmap.selectedMapId = selectedMapId;
        }

        return bmap.selectedMapId;
    };

    return (
        <div className="esri-component customBaseMapGallery">
            <div className={classnames("basemaps-container", { "maps-collapsed": !expanded })}>
                {expanded && configA.basemaps.length !== 1 ? (
                    <div className={classnames("map-container-body", { "overlay-mode": isOverlayModeActive })}>
                        {configA.basemaps
                            .filter((basemap: ICustomBaseMap) => basemap.parentId === "")
                            .map((basemap: ICustomBaseMap) => (
                                <BaseMapItem
                                    key={basemap.id}
                                    basemap={basemap}
                                    classNameDefault={"basemap"}
                                    classNameSelected={"selected-basemap"}
                                    selectedMapId={getSelectedMapId(basemap, primaryMapId)}
                                    toggleExpanded={toggleExpanded}
                                />
                            ))}
                    </div>
                ) : (
                    <BaseMapButton
                        activeBasemapID={primaryMapId}
                        basemaps={configA.basemaps}
                        toggleExpanded={toggleExpanded}
                    ></BaseMapButton>
                )}
            </div>
            {subBasemapConfig ? <ChildMapsPopup /> : null}
        </div>
    );
};
